import isEmpty from 'lodash/isEmpty'
import { useCallback, useEffect, useState } from 'react'
import { useLocalStorage } from 'react-use'
import type { UIMessage } from '@ai-sdk/react'
import { generateId, truncate } from '@ztechapps/common.utils'
import type { ChatSession, SessionStore } from '../types'
import {
  SESSION_STORAGE_KEY,
  SESSION_SCHEMA_VERSION,
  MAX_SESSIONS,
  MAX_TITLE_LENGTH,
  DEFAULT_SESSION_TITLE,
} from '../constants'

export type UseChatSessionsResult = {
  /** All stored sessions, sorted by most recently updated */
  sessions: ChatSession[]
  /** Currently active session, or null if none exists */
  currentSession: ChatSession | null
  /**
   * Create a new session and switch to it immediately.
   * If MAX_SESSIONS limit is reached, deletes the least recently updated session.
   * @returns The newly created session
   */
  createSession: () => ChatSession
  /**
   * Delete a session by ID.
   * If deleted session was current, switches to most recently updated session.
   * If no sessions remain after deletion, creates a new empty session.
   * @param _id - The session ID to delete
   */
  deleteSession: (_id: string) => void
  /**
   * Delete all sessions and create a new empty session.
   * Cannot leave the user with zero sessions.
   */
  deleteAllSessions: () => void
  /**
   * Update session metadata (title, messages, etc.)
   * Automatically updates the session's updatedAt timestamp.
   * @param _id - The session ID to update
   * @param updates - Partial session data to merge
   */
  updateSession: (_id: string, updates: Partial<ChatSession>) => void
  /** Get the currently active session, or null if none */
  getCurrentSession: () => ChatSession | null
  /**
   * Switch to a different session by ID.
   * Does nothing if session ID doesn't exist.
   * @param _id - The session ID to switch to
   */
  setCurrentSession: (_id: string) => void
  /**
   * Sync current messages to the active session.
   * Automatically generates session title from first user message if title is still default.
   * Updates the session's updatedAt timestamp.
   * @param messages - Array of messages to sync
   */
  syncMessages: (messages: UIMessage[]) => void
  /**
   * Whether the session store has been initialized from localStorage.
   * Use this to prevent race conditions when reading session data on mount.
   */
  isInitialized: boolean
}

/**
 * Generate a session title from the first user message.
 * Format: "{index} - {truncated message}"
 *
 * @param firstMessage - The first user message content
 * @param index - The session's serial number
 * @returns Formatted session title (e.g., "1 - Hello world")
 *
 * @example
 * generateSessionTitle("Hello world, this is a very long message", 1)
 * // Returns: "1 - Hello world, this is a very long mess..."
 */
const generateSessionTitle = (firstMessage: string, index: number): string => {
  const trimmed = firstMessage.trim()
  const truncated = truncate(trimmed, MAX_TITLE_LENGTH)
  return `${index} - ${truncated}`
}

/**
 * Create a new empty session with the next available index.
 * This helper ensures consistent session creation across all operations.
 *
 * @param nextIndex - The index number to assign to the new session
 * @returns A new ChatSession with default values and current timestamp
 */
const createEmptySession = (nextIndex: number): ChatSession => ({
  _id: generateId(),
  index: nextIndex,
  title: DEFAULT_SESSION_TITLE,
  messages: [],
  createdAt: Date.now(),
  updatedAt: Date.now(),
})

/**
 * Create an initial empty store with a single default session.
 * Used when localStorage is empty or corrupted.
 *
 * @returns Initial store with version 1, first session, and nextIndex of 2
 */
const createInitialStore = (): SessionStore => {
  const firstSession = createEmptySession(1)

  return {
    version: SESSION_SCHEMA_VERSION,
    currentSessionId: firstSession._id,
    nextIndex: 2,
    sessions: [firstSession],
  }
}

/**
 * Safe wrapper around setStore that handles localStorage quota exceeded errors.
 *
 * **Quota Handling Strategy:**
 * 1. Try to save the new store
 * 2. If QuotaExceededError occurs, progressively delete oldest sessions
 * 3. Retry save after each deletion until successful or only 1 session remains
 * 4. If single session is too large, throw error for error boundary handling
 *
 * **Error Handling:**
 * - QuotaExceededError: Attempts automatic recovery by deleting old sessions
 * - Other errors: Re-thrown for error boundary handling
 *
 * @param setStoreFn - The useLocalStorage setter function
 * @param newStore - The new store state to save
 * @param _currentStore - Current store (unused, kept for future migrations)
 * @throws Error if unable to save even a single session due to quota
 */
const safeSetStore = (
  setStoreFn: (value: SessionStore) => void,
  newStore: SessionStore,
  _currentStore: SessionStore | undefined
): void => {
  try {
    setStoreFn(newStore)
  } catch (error) {
    // Handle quota exceeded error
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      // Delete oldest sessions until we can save
      const sortedByUpdatedAt = [...newStore.sessions].sort((a, b) => a.updatedAt - b.updatedAt)

      // Try removing sessions one by one until save succeeds
      for (let i = 0; i < sortedByUpdatedAt.length - 1; i++) {
        const remainingSessions = sortedByUpdatedAt.slice(i + 1)
        const reducedStore: SessionStore = {
          ...newStore,
          sessions: remainingSessions,
        }

        try {
          setStoreFn(reducedStore)
          // Successfully saved after removing old sessions
          return
        } catch {
          // Continue trying with fewer sessions
        }
      }

      // If we get here, even a single session is too large
      // This is a critical failure - throw error so it can be caught by error boundaries
      throw new Error('Unable to save chat session: storage quota exceeded. Please clear browser data or old sessions.')
    }
    // Re-throw other errors so they can be caught by error boundaries
    throw error
  }
}

/**
 * Hook for managing chat sessions with localStorage persistence
 */
export const useChatSessions = (): UseChatSessionsResult => {
  const [store, setStore] = useLocalStorage<SessionStore>(SESSION_STORAGE_KEY, createInitialStore(), {
    raw: false,
    serializer: JSON.stringify,
    deserializer: JSON.parse,
  })

  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize store on mount if it doesn't exist or is corrupted
  useEffect(() => {
    try {
      if (!store || !store.sessions || isEmpty(store.sessions)) {
        const initialStore = createInitialStore()
        safeSetStore(setStore, initialStore, store)
      }
      setIsInitialized(true)
    } catch {
      // Handle corrupted data - reinitialize with fresh store
      const initialStore = createInitialStore()
      safeSetStore(setStore, initialStore, store)
      setIsInitialized(true)
    }
    // Mount-only by design (see the comment above) — `store`/`setStore` are read as a one-time
    // snapshot to detect a missing/corrupted session store; depending on `store` would re-run
    // this check on every session write this hook itself performs (create/delete/update).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Get current session
  const getCurrentSession = useCallback((): ChatSession | null => {
    if (!store) return null
    return store.sessions.find((s) => s._id === store.currentSessionId) ?? null
  }, [store])

  // Create a new session
  const createSession = useCallback((): ChatSession => {
    if (!store) {
      const initialStore = createInitialStore()
      safeSetStore(setStore, initialStore, store)
      return initialStore.sessions[0]
    }

    // Enforce session limit - delete least recently updated session if at limit
    let updatedSessions = [...store.sessions]
    if (updatedSessions.length >= MAX_SESSIONS) {
      // Sort by updatedAt ascending to find least recently updated
      const sortedByUpdatedAt = [...updatedSessions].sort((a, b) => a.updatedAt - b.updatedAt)
      const leastRecentlyUpdated = sortedByUpdatedAt[0]
      updatedSessions = updatedSessions.filter((s) => s._id !== leastRecentlyUpdated._id)
    }

    const newSession = createEmptySession(store.nextIndex)

    const newStore: SessionStore = {
      ...store,
      currentSessionId: newSession._id,
      nextIndex: store.nextIndex + 1,
      sessions: [newSession, ...updatedSessions],
    }

    safeSetStore(setStore, newStore, store)
    return newSession
  }, [store, setStore])

  // Delete a session by ID
  const deleteSession = useCallback(
    (_id: string) => {
      if (!store) return

      const remainingSessions = store.sessions.filter((s) => s._id !== _id)

      // If no sessions remain, create a new one
      if (isEmpty(remainingSessions)) {
        const newSession = createEmptySession(store.nextIndex)

        safeSetStore(
          setStore,
          {
            ...store,
            currentSessionId: newSession._id,
            nextIndex: store.nextIndex + 1,
            sessions: [newSession],
          },
          store
        )
        return
      }

      // If deleted session was current, switch to most recently updated
      let newCurrentSessionId = store.currentSessionId
      if (_id === store.currentSessionId) {
        const sortedByUpdatedAt = [...remainingSessions].sort((a, b) => b.updatedAt - a.updatedAt)
        newCurrentSessionId = sortedByUpdatedAt[0]._id
      }

      safeSetStore(
        setStore,
        {
          ...store,
          currentSessionId: newCurrentSessionId,
          sessions: remainingSessions,
        },
        store
      )
    },
    [store, setStore]
  )

  // Delete all sessions
  const deleteAllSessions = useCallback(() => {
    if (!store) return

    const newSession = createEmptySession(store.nextIndex)

    safeSetStore(
      setStore,
      {
        ...store,
        currentSessionId: newSession._id,
        nextIndex: store.nextIndex + 1,
        sessions: [newSession],
      },
      store
    )
  }, [store, setStore])

  // Update session metadata
  const updateSession = useCallback(
    (_id: string, updates: Partial<ChatSession>) => {
      if (!store) return

      const updatedSessions = store.sessions.map((session) => {
        if (session._id === _id) {
          return {
            ...session,
            ...updates,
            updatedAt: Date.now(),
          }
        }
        return session
      })

      safeSetStore(
        setStore,
        {
          ...store,
          sessions: updatedSessions,
        },
        store
      )
    },
    [store, setStore]
  )

  // Set current session
  const setCurrentSession = useCallback(
    (_id: string) => {
      if (!store) return

      const sessionExists = store.sessions.some((s) => s._id === _id)
      if (!sessionExists) {
        // Session not found - silently fail
        return
      }

      safeSetStore(
        setStore,
        {
          ...store,
          currentSessionId: _id,
        },
        store
      )
    },
    [store, setStore]
  )

  // Sync messages to current session
  const syncMessages = useCallback(
    (messages: UIMessage[]) => {
      if (!store) return

      const currentSession = getCurrentSession()
      if (!currentSession) return

      // Check if this is the first user message and title is still default
      const hasFirstUserMessage = messages.some((m) => m.role === 'user')
      const shouldUpdateTitle = currentSession.title === DEFAULT_SESSION_TITLE && hasFirstUserMessage

      let newTitle = currentSession.title
      if (shouldUpdateTitle) {
        const firstUserMessage = messages.find((m) => m.role === 'user')
        if (firstUserMessage) {
          // Extract text content from message parts
          const textParts =
            firstUserMessage.parts?.filter(
              (part): part is Extract<typeof part, { type: 'text' }> => part.type === 'text'
            ) ?? []
          const textContent = textParts.map((part) => part.text).join(' ')

          if (textContent) {
            newTitle = generateSessionTitle(textContent, currentSession.index)
          }
        }
      }

      const updatedSessions = store.sessions.map((session) => {
        if (session._id === currentSession._id) {
          return {
            ...session,
            messages,
            title: newTitle,
            updatedAt: Date.now(),
          }
        }
        return session
      })

      safeSetStore(
        setStore,
        {
          ...store,
          sessions: updatedSessions,
        },
        store
      )
    },
    [store, setStore, getCurrentSession]
  )

  return {
    sessions: store?.sessions ?? [],
    currentSession: getCurrentSession(),
    createSession,
    deleteSession,
    deleteAllSessions,
    updateSession,
    getCurrentSession,
    setCurrentSession,
    syncMessages,
    isInitialized,
  }
}
